import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ShieldCheck, UserX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { createFirm, fetchUsers, setUserFirm, updateUser } from '@/services/admin';
import type { AdminFirm, AdminUser } from '@/services/types';

const AGENCIES = ['anatel', 'cade', 'aneel', 'ans', 'ibama', 'bacen', 'cvm'] as const;
const ROLES = ['viewer', 'analyst', 'admin', 'superadmin', 'visitante'] as const;
/** The roles whose agency list the app never consults. */
const PRIVILEGED_ROLES: readonly string[] = ['admin', 'superadmin', 'visitante'];

/**
 * Who can reach this application, and what they can actually see.
 *
 * Every access change used to mean SSH plus hand-written SQL against production.
 * That is how the `agencies` trap was found: `may_access` is
 * `role in ('admin', 'superadmin', 'visitante') || agencies.includes(agency)`,
 * so clearing somebody's agency list looks like a revocation and does nothing at
 * all while they hold one of those roles. The array would read empty in the
 * database while the account still reached every corpus.
 *
 * So this screen never shows the array alone. Each row states what the app
 * ENFORCES, and an admin row says plainly that its agency list is not consulted.
 *
 * Two things it deliberately cannot do. There is no delete: revoking is
 * `is_active = false`, which takes effect on the target's next request and is
 * reversible, while dropping the row would destroy `last_login_at` and
 * `created_at` with no undo. And nobody can edit their own account -- the backend
 * refuses it, and the controls are disabled here so the refusal is understood
 * before it is provoked rather than after.
 */
export function AdminUsersPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: fetchUsers,
  });

  const mutation = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Parameters<typeof updateUser>[1] }) =>
      updateUser(id, changes),
    onSuccess: () => {
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    // A guard refusal is a rule being enforced, not a crash. It carries its own
    // sentence explaining which lockout it prevented, so that is what is shown.
    onError: (err: unknown) =>
      setFailure(err instanceof ApiError ? err.message : t('admin:users.unknownError')),
  });
  // A firm is not access, so it is not under the self-edit rule: a superadmin may put their
  // own account in a firm.
  const firmMutation = useMutation({
    mutationFn: ({ id, firmId }: { id: string; firmId: string | null }) => setUserFirm(id, firmId),
    onSuccess: () => {
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err: unknown) =>
      setFailure(err instanceof ApiError ? err.message : t('admin:users.unknownError')),
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('admin:users.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('admin:users.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="admin:users.explainer" />

      <FieldGuide i18nKey="admin:users.guide" level={2} />

      {failure ? (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{failure}</span>
        </p>
      ) : null}

      <FirmsCard
        firms={data.firms ?? []}
        onError={(message) => setFailure(message)}
      />

      <p className="text-xs text-muted-foreground" aria-live="polite">
        {t('admin:users.summary', {
          total: data.users.length,
          active: data.users.filter((user) => user.is_active).length,
        })}
      </p>

      <ul className="space-y-2">
        {data.users.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            isSelf={user.id === data.actor_id}
            busy={mutation.isPending}
            dateFormat={dateFormat}
            onChange={(changes) => mutation.mutate({ id: user.id, changes })}
            firms={data.firms ?? []}
            onFirm={(firmId) => firmMutation.mutate({ id: user.id, firmId })}
            firmBusy={firmMutation.isPending}
          />
        ))}
      </ul>
    </div>
  );
}

/** The firms: accounts in one firm share its client folders and its bulletin draft. */
function FirmsCard({
  firms,
  onError,
}: {
  firms: AdminFirm[];
  onError: (message: string) => void;
}) {
  const { t } = useTranslation(['admin']);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => createFirm(name.trim()),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err: unknown) =>
      onError(err instanceof ApiError ? err.message : t('admin:users.unknownError')),
  });
  return (
    <section className="space-y-2 rounded-lg border p-3 text-xs" data-firms>
      <h2 className="text-sm font-semibold text-foreground">{t('admin:users.firms.title')}</h2>
      <p className="text-muted-foreground">{t('admin:users.firms.help')}</p>
      {firms.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {firms.map((firm) => (
            <li key={firm.id} className="rounded border px-2 py-1 text-foreground">
              {firm.name}{' '}
              <span className="text-muted-foreground">
                {t('admin:users.firms.members', { count: firm.members })}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">{t('admin:users.firms.none')}</p>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <label className="space-y-1 text-muted-foreground">
          {t('admin:users.firms.newName')}
          <input
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            className="block w-64 rounded-md border bg-background px-2 py-1 text-sm text-foreground"
            data-new-firm
          />
        </label>
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="rounded-md border px-3 py-1 text-foreground disabled:opacity-40"
        >
          {t('admin:users.firms.create')}
        </button>
      </form>
    </section>
  );
}

function UserRow({
  user,
  isSelf,
  busy,
  dateFormat,
  onChange,
  firms,
  onFirm,
  firmBusy,
}: {
  user: AdminUser;
  isSelf: boolean;
  busy: boolean;
  dateFormat: Intl.DateTimeFormat;
  onChange: (changes: { role?: string; is_active?: boolean; agencies?: string[] }) => void;
  firms: AdminFirm[];
  onFirm: (firmId: string | null) => void;
  firmBusy: boolean;
}) {
  const { t } = useTranslation(['admin']);
  const disabled = isSelf || busy;

  return (
    <li
      className={`space-y-3 rounded-lg border p-3 text-xs ${
        user.is_active ? 'bg-card' : 'border-dashed bg-muted/30'
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="text-sm text-foreground">{user.full_name || user.email}</span>
          {user.full_name ? (
            <span className="ml-2 text-muted-foreground">{user.email}</span>
          ) : null}
          {isSelf ? (
            <span className="ml-2 rounded border px-1.5 py-0.5 text-foreground">
              {t('admin:users.you')}
            </span>
          ) : null}
        </div>
        <span className="flex items-center gap-2 text-muted-foreground">
          {user.is_active ? (
            <span className="flex items-center gap-1 text-foreground">
              <ShieldCheck className="h-3 w-3" aria-hidden />
              {t('admin:users.active')}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <UserX className="h-3 w-3" aria-hidden />
              {t('admin:users.revoked')}
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-foreground">
          {t('admin:users.role')}
          <select
            aria-label={t('admin:users.roleFor', { name: user.email })}
            value={user.role}
            disabled={disabled}
            onChange={(event) => onChange({ role: event.target.value })}
            className="rounded-md border bg-background px-2 py-1 text-foreground disabled:opacity-40"
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {t(`admin:users.roles.${role}`)}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="flex flex-wrap items-center gap-3" disabled={disabled}>
          <legend className="sr-only">{t('admin:users.agenciesFor', { name: user.email })}</legend>
          {AGENCIES.map((agency) => (
            <label key={agency} className="flex items-center gap-1.5 text-foreground">
              <input
                type="checkbox"
                checked={user.agencies.includes(agency)}
                aria-label={`${agency} — ${user.email}`}
                onChange={(event) =>
                  onChange({
                    agencies: event.target.checked
                      ? [...user.agencies, agency]
                      : user.agencies.filter((slug) => slug !== agency),
                  })
                }
                className="h-4 w-4 rounded border disabled:opacity-40"
              />
              {agency.toUpperCase()}
            </label>
          ))}
        </fieldset>

        <label className="flex items-center gap-2 text-foreground">
          {t('admin:users.firms.label')}
          <select
            aria-label={t('admin:users.firms.for', { name: user.email })}
            value={user.firm_id ?? ''}
            disabled={firmBusy}
            onChange={(event) => onFirm(event.target.value || null)}
            className="rounded-md border bg-background px-2 py-1 text-foreground disabled:opacity-40"
            data-user-firm
          >
            <option value="">{t('admin:users.firms.noFirm')}</option>
            {firms.map((firm) => (
              <option key={firm.id} value={firm.id}>
                {firm.name}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ is_active: !user.is_active })}
          className="rounded-md border px-3 py-1 text-foreground disabled:opacity-40"
        >
          {user.is_active ? t('admin:users.revoke') : t('admin:users.restore')}
        </button>
      </div>

      {/* The whole reason this screen exists. An admin's or superadmin's agency
          list is not consulted, so showing it without this line would invite
          somebody to untick three boxes and believe they had revoked something. */}
      {PRIVILEGED_ROLES.includes(user.role) ? (
        <p className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-foreground">
          {t('admin:users.adminOverride')}
        </p>
      ) : null}

      <p className="text-muted-foreground">
        {t('admin:users.lastLogin', {
          when: user.last_login_at
            ? dateFormat.format(new Date(user.last_login_at))
            : t('admin:users.never'),
        })}
      </p>
    </li>
  );
}
